import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

/**
 * Phone Authentication Service
 * Handles Firebase Phone Auth (SMS OTP) via @capacitor-firebase/authentication.
 * Used for: (1) phone verification during registration, (2) OTP-based login.
 *
 * IMPORTANT: signInWithPhoneNumber() returns void — the verificationId arrives
 * asynchronously via the 'phoneCodeSent' event. On Android, auto-verification
 * may fire 'phoneVerificationCompleted' instead (no code entry needed).
 */
export const phoneAuthService = {
  /**
   * Send SMS verification code to phone number.
   *
   * On Android a single send fires TWO native callbacks: `phoneCodeSent`
   * (≈1-2s, gives the verificationId for manual entry) and, ≈15s later when
   * SmsRetriever auto-reads the SMS, `phoneVerificationCompleted` — which the
   * native plugin uses to sign in, CONSUMING the code server-side. If we stop
   * listening after `phoneCodeSent`, that late auto-verification silently
   * consumes the code and the user's manual `confirmCode` then fails with
   * `auth/code-expired`. So we keep the completion listener alive and surface
   * late auto-verification via `onAutoVerified` so the caller can log in
   * silently instead of leaving the user on a dead OTP screen.
   *
   * @param phoneNumber - Full E.164 format, e.g., "+919876543210"
   * @param onAutoVerified - called if auto-verification completes AFTER the
   *   verificationId was already delivered (i.e. the OTP screen is showing).
   * @returns { verificationId, autoVerified } - autoVerified=true means instant
   *   verification completed before any code entry was needed.
   */
  sendVerificationCode: async (
    phoneNumber: string,
    onAutoVerified?: () => void,
  ): Promise<{
    verificationId: string | null;
    autoVerified: boolean;
  }> => {
    return new Promise(async (resolve, reject) => {
      // Clean up any existing listeners
      await FirebaseAuthentication.removeAllListeners();

      // Tracks whether the initial promise has resolved/rejected. After it
      // settles via `phoneCodeSent`, the completion listener stays active.
      let settled = false;

      // Listen for SMS code sent — provides verificationId for manual entry.
      // Do NOT remove listeners here: auto-verification may still complete.
      await FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
        console.log('[PhoneAuth] Code sent, verificationId received');
        if (settled) return;
        settled = true;
        resolve({ verificationId: event.verificationId, autoVerified: false });
      });

      // Listen for auto-verification (Android) — no code entry needed.
      await FirebaseAuthentication.addListener('phoneVerificationCompleted', async () => {
        console.log('[PhoneAuth] Auto-verification completed');
        await FirebaseAuthentication.removeAllListeners();
        if (settled) {
          // Late auto-verification: OTP screen already shown. Caller logs in.
          onAutoVerified?.();
        } else {
          // Instant verification before the code screen was needed.
          settled = true;
          resolve({ verificationId: null, autoVerified: true });
        }
      });

      // Listen for verification failure
      await FirebaseAuthentication.addListener('phoneVerificationFailed', async (event) => {
        console.error('[PhoneAuth] Verification failed:', event.message);
        if (settled) return; // post-codeSent failures are non-fatal (manual entry still works)
        settled = true;
        await FirebaseAuthentication.removeAllListeners();
        reject(new Error(event.message || 'Phone verification failed'));
      });

      // Start the phone auth flow
      try {
        await FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber });
      } catch (err) {
        await FirebaseAuthentication.removeAllListeners();
        if (settled) return;
        settled = true;
        reject(err);
      }
    });
  },

  /**
   * Confirm the SMS code and get Firebase ID token.
   * @param verificationId - from phoneCodeSent event
   * @param verificationCode - 6-digit code from SMS
   * @returns Firebase ID token string
   */
  confirmCode: async (verificationId: string, verificationCode: string): Promise<string> => {
    // Stop any pending auto-verification listener so it can't race with the
    // manual confirm we're about to perform.
    await FirebaseAuthentication.removeAllListeners();
    const result = await FirebaseAuthentication.confirmVerificationCode({
      verificationId,
      verificationCode,
    });
    console.log('[PhoneAuth] confirmVerificationCode result:', JSON.stringify(result));
    const { token } = await FirebaseAuthentication.getIdToken();
    console.log('[PhoneAuth] getIdToken result:', token ? `token(${token.length} chars)` : 'EMPTY');
    return token;
  },

  /**
   * Get Firebase ID token (used after auto-verification).
   */
  getIdToken: async (forceRefresh = false): Promise<string> => {
    const { token } = await FirebaseAuthentication.getIdToken({ forceRefresh });
    return token;
  },

  /**
   * Sign out from Firebase after getting the token.
   */
  signOut: async (): Promise<void> => {
    await FirebaseAuthentication.signOut();
  },

  /**
   * Remove all phone-auth listeners. Call when the OTP UI closes/unmounts so a
   * pending auto-verification listener doesn't fire after the user left.
   */
  cleanup: async (): Promise<void> => {
    await FirebaseAuthentication.removeAllListeners();
  },
};
